import { auth, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';

/**
 * Triggers AI paragraph generation for a single report section by calling
 * the GenerateDraftWithLLM Cloud Function.
 *
 * Data-readiness strategy:
 *   - When `dataReadyPromise` is provided (managed navigation handlers),
 *     the Cloud Function call is chained directly to it — firing as soon as
 *     Firestore confirms the write.  No arbitrary delay.
 *   - When `dataReadyPromise` is null (unmanaged exits via
 *     useSectionExitRegeneration), a legacy 4-second delay is used as a
 *     safety buffer since the caller can't provide a flush Promise.
 *   - If `dataReadyPromise` rejects (flush failure), we fall back to the
 *     4-second delay so auto-flush / retry mechanisms still have a chance
 *     to commit the data before the Cloud Function reads it.
 *
 * The entire pipeline is wrapped in a .catch() so failures are logged but
 * never surface as unhandled Promise rejections.
 *
 * @param {string}       patientId           - Firestore patient document ID
 * @param {string}       currentStageTitle   - e.g. 'Clinical Interview'
 * @param {string}       currentSectionTitle - e.g. 'Presenting Complaint'
 * @param {string[]}     conditions          - optional condition flags
 * @param {Promise|null} dataReadyPromise    - resolves when Firestore data
 *                                             is committed; null = use 4s delay
 */
export const TriggerGenerateDocumentSection = (
  patientId,
  currentStageTitle,
  currentSectionTitle,
  conditions = [],
  dataReadyPromise = null,
) => {
  const GenerateDraftWithLLM = httpsCallable(functions, 'GenerateDraftWithLLM');
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const user = auth.currentUser;
  if (!user || !user.uid) {
    console.warn(
      '[GenDraft] Skipped — no authenticated user for',
      currentStageTitle, '/', currentSectionTitle,
    );
    return;
  }

  // Trace the request the moment it is queued (before the data-ready wait) so
  // we can see, per stage/section, whether a managed flush or the legacy delay
  // was used. Pairs with the per-attempt logs below. Remove with the rest of
  // the Autism testing logs before production.
  console.log('[GenDraft] queued', {
    patientId,
    stage: currentStageTitle,
    section: currentSectionTitle,
    conditions,
    mode: dataReadyPromise ? 'managed-flush' : 'legacy-delay',
  });

  const LEGACY_DELAY_MS = 4000;
  const RETRY_BASE_DELAY_MS = 2000;
  const MAX_RETRIES = 3;
  const TIMEOUT_DURATION = 5 * 60 * 1000; // 5 minutes

  // Build the "wait for data" signal.  Managed callers pass the flush
  // Promise so we can fire immediately once data is confirmed committed.
  // Unmanaged callers (hook cleanup) omit it, falling back to a fixed
  // delay that gives the auto-flush mechanism time to complete.
  const dataReady = dataReadyPromise
    ? dataReadyPromise.catch((err) => {
        console.warn(
          '[TriggerGenerate] Flush failed, falling back to legacy delay:',
          err,
        );
        return delay(LEGACY_DELAY_MS);
      })
    : delay(LEGACY_DELAY_MS);

  dataReady
    .then(() => {
      console.log(
        'Triggering paragraph generations for section:',
        currentSectionTitle,
      );
      if (conditions.length > 0) {
        console.log('With conditions:', conditions);
      }

      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error('Operation timed out after 5 minutes'));
        }, TIMEOUT_DURATION);
      });

      const attemptGeneration = async (attempt = 1) => {
        try {
          // Only delay on retries — on the first attempt data is already
          // confirmed committed (via dataReadyPromise) or the legacy 4s
          // delay has elapsed, so an additional wait is unnecessary.
          if (attempt > 1) {
            await delay(RETRY_BASE_DELAY_MS * attempt);
          }

          const reportParams = {
            clinicianId: user.uid,
            patientId,
            reportStage: currentStageTitle,
            reportSection: currentSectionTitle,
            isTest: false,
            conditions,
          };

          const result = await Promise.race([
            GenerateDraftWithLLM(reportParams),
            timeoutPromise,
          ]);

          console.log('Function response:', result.data);
          return result;
        } catch (error) {
          console.error(`Attempt ${attempt} failed:`, error);

          if (error.message === 'Operation timed out after 5 minutes') {
            throw new Error(
              'The operation took too long to complete. Please try again.',
            );
          }

          if (
            error.code === 'functions/resource-exhausted' &&
            attempt < MAX_RETRIES
          ) {
            console.log(
              `Retrying in ${(RETRY_BASE_DELAY_MS * (attempt + 1)) / 1000} seconds...`,
            );
            return attemptGeneration(attempt + 1);
          }

          throw error;
        }
      };

      return attemptGeneration();
    })
    .catch((error) => {
      console.error('[TriggerGenerate] Generation pipeline failed:', error);
    });
};
